import { useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { LineBreak } from '@direct-frontend/components';
import { compact } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { VerticalMenu, type VerticalMenuProps } from '#shared/components/VerticalMenu';
import { useYandexKit } from '#shared/components/YandexKitProvider';
import { constructorTestIds } from '#shared/lib/testids';
import { useAllowedToDeleteSection } from '../../hooks/useAllowedToDeleteSection';
import { useAllowedToPasteAddSection } from '../../hooks/useAllowedToPasteAddSection';
import { useAllowedToPasteReplaceSection } from '../../hooks/useAllowedToPasteReplaceSection';
import { useChangeConstructorRightBarSection } from '../../hooks/useChangeConstructorRightBarSection';
import { useDenormalizedConstructorSection } from '../../hooks/useDenormalizedConstructorSection';
import { constructorProductLoggingEvents } from '../../lib/constructorProductLoggingEvents';
import { CONSTRUCTOR_MYSTIQUE_ID } from '../../model/constants';
import {
    addConstructorModule,
    addConstructorSection,
    changeConstructorModule,
    deleteConstructorModule,
    getConstructorSectionCount,
    makeConstructorSectionParent,
    useConstructorRightBarSection,
    useConstructorSectionSettings,
} from '../../model/slices';
import { useConstructorContext } from '../ConstructorProvider';

type ConstructorSectionItemMenu = Omit<VerticalMenuProps, 'items'> & {
    pageId: string;
    sectionInstanceId: string;
    moduleId?: string;
    areTemplatesEnabled?: boolean;
};

export const ConstructorSectionItemMenu = (props: ConstructorSectionItemMenu) => {
    const { pageId, sectionInstanceId, moduleId, areTemplatesEnabled, ...rest } = props;

    const section = useConstructorSectionSettings(pageId, sectionInstanceId, undefined, true);
    const module = useConstructorSectionSettings(pageId, sectionInstanceId, moduleId);
    const currentSection = useConstructorRightBarSection();
    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();

    const yandexKit = useYandexKit();

    const sectionSettings = useMemo(() => yandexKit.getSettings(section.widget), [yandexKit, section]);

    const allowedToDelete = useAllowedToDeleteSection({ pageId, sectionInstanceId });

    const {
        detachSection,
        deleteSection,
        copySection,
        checkCopiedSection,
        copiedSection,
        openAddSectionPopup,
        replaceSection,
        checkForLimits,
        showLimit,
        limits,
    } = useConstructorContext();

    const denormalizeSection = useDenormalizedConstructorSection();

    const allowedToAdd = useAllowedToPasteAddSection({ pageId, sectionInstanceId });

    const allowedToReplace = useAllowedToPasteReplaceSection({ pageId, sectionInstanceId });

    const deleteSectionCallback = useCallback(() => {
        deleteSection(pageId, sectionInstanceId);

        if (currentSection?.sectionInstanceId === sectionInstanceId) {
            changeConstructorRightBarSection();
        }
    }, [pageId, sectionInstanceId, currentSection, changeConstructorRightBarSection, deleteSection]);

    const deleteModuleCallback = useCallback(
        (moduleId: string) => {
            changeConstructorRightBarSection(sectionInstanceId);
            deleteConstructorModule(pageId, sectionInstanceId, moduleId);
        },
        [pageId, sectionInstanceId, changeConstructorRightBarSection]
    );

    const replaceSectionHandler = useCallback(
        (newSectionSettings: Object) => {
            replaceSection({ pageId, sectionInstanceId, newSectionSettings });
        },
        [pageId, sectionInstanceId, replaceSection]
    );

    const replaceModule = useCallback(
        (newModuleSettings: Object) => {
            if (!moduleId || !module) {
                return;
            }

            changeConstructorModule(pageId, sectionInstanceId, moduleId, {
                ...newModuleSettings,
                id: moduleId,
                display_sequence: module.display_sequence,
            });

            if (currentSection?.sectionInstanceId === sectionInstanceId && currentSection?.moduleId === moduleId) {
                changeConstructorRightBarSection();
            } else {
                changeConstructorRightBarSection(sectionInstanceId, moduleId);
            }
        },
        [pageId, sectionInstanceId, moduleId, module, changeConstructorRightBarSection, currentSection]
    );

    const modules = useMemo(
        () => (get(sectionSettings, 'properties.modules') ? get(section, 'settings.modules', []) : undefined),
        [section, sectionSettings]
    );

    const addModuleCallback = useCallback(
        (settings: Object, newDisplaySequence?: number) => {
            if (Array.isArray(modules)) {
                if (modules.length >= limits.module) {
                    showLimit('module');

                    return;
                }

                const baseTitle = get(sectionSettings, 'properties.modules.title') || 'Элемент';
                const newId = self.crypto.randomUUID();

                addConstructorModule(pageId, sectionInstanceId, newId, {
                    title: baseTitle,
                    ...settings,
                    id: newId,
                    display_sequence:
                        newDisplaySequence || Math.min(...modules.map((module) => module.display_sequence), 1) - 0.01,
                });
                changeConstructorRightBarSection(sectionInstanceId, newId);
            }
        },
        [modules, pageId, sectionSettings, sectionInstanceId, changeConstructorRightBarSection, showLimit, limits]
    );

    const replaceWithMystique = useCallback(() => {
        let widgetToReplace;

        if (section.widget === CONSTRUCTOR_MYSTIQUE_ID) {
            const denormalizedSection = denormalizeSection(section);

            widgetToReplace =
                denormalizedSection && 'template' in denormalizedSection
                    ? denormalizedSection.template.alias
                    : undefined;
        } else {
            widgetToReplace = section.widget;
        }

        openAddSectionPopup({ newDisplaySequence: 0, addCallback: replaceSectionHandler, widgetToReplace });
    }, [openAddSectionPopup, replaceSectionHandler, section, denormalizeSection]);

    const sectionItems = useMemo(() => {
        const { totalCount, count } = getConstructorSectionCount(section.widget);

        const maximumCheck =
            sectionSettings?.maximum === 1 || (sectionSettings?.maximum ? count < sectionSettings.maximum : true);

        const maximumPerWebsiteCheck =
            sectionSettings?.totalMaximum === 1 ||
            (sectionSettings?.totalMaximum ? totalCount < sectionSettings.totalMaximum : true);

        const copiedSectionSettings = copiedSection
            ? yandexKit.getSettings(
                  denormalizeSection({
                      ...copiedSection.sectionSettings,
                      instance_id: copiedSection.sectionInstanceId,
                  })
              )
            : null;

        const cannotCopy = sectionSettings?.canCopy === false;

        const canDetach = section.section_type !== 'usual' && sectionSettings?.canBeParent !== false;

        const canBeParent = section.section_type !== 'parent' && sectionSettings?.canBeParent !== false;

        const canBeReplacedWithMystique = areTemplatesEnabled;

        return compact([
            allowedToReplace || allowedToAdd.allowed || allowedToAdd.message
                ? {
                      key: 'copyOrReplace',
                      options: compact([
                          allowedToReplace
                              ? {
                                    value: 'replace',
                                    content: 'Заменить',
                                    onAction: () => {
                                        if (!copiedSection) {
                                            return;
                                        }

                                        replaceSectionHandler(copiedSection.sectionSettings);
                                    },
                                }
                              : null,
                          allowedToAdd.allowed || allowedToAdd.message
                              ? {
                                    value: 'paste',
                                    content: allowedToAdd.pasteNext ? 'Вставить секцию ниже' : 'Вставить',
                                    caption: allowedToAdd.message,
                                    onAction: () => {
                                        if (!copiedSection) {
                                            return;
                                        }

                                        if (copiedSection.moduleId) {
                                            logEventString(constructorProductLoggingEvents.Section.Paste, 'module', {
                                                widget: copiedSection.widget,
                                            });
                                            addModuleCallback(copiedSection.sectionSettings);
                                        } else {
                                            const limit = checkForLimits('section');

                                            if (limit) {
                                                showLimit(limit);

                                                return;
                                            }

                                            logEventString(constructorProductLoggingEvents.Section.Paste, 'section', {
                                                widget: copiedSection.widget,
                                                pasteBelow: String(allowedToAdd.pasteNext),
                                            });

                                            addConstructorSection(
                                                pageId,
                                                {
                                                    widget: copiedSection.sectionSettings.widget,
                                                    settings: copiedSection.sectionSettings.settings,
                                                    display_sequence: allowedToAdd.pasteNext
                                                        ? section.display_sequence + 0.01
                                                        : copiedSection.sectionSettings.display_sequence,
                                                    id:
                                                        copiedSection.sectionSettings.section_type === 'usual'
                                                            ? undefined
                                                            : copiedSection.sectionSettings.id,
                                                    section_type:
                                                        copiedSection.sectionSettings.section_type === 'usual'
                                                            ? undefined
                                                            : 'child',
                                                    template_id: copiedSection.sectionSettings.template_id,
                                                    status: copiedSection.sectionSettings.status,
                                                },
                                                copiedSectionSettings?.totalMaximum === 1
                                            );
                                        }
                                    },
                                    disabled: !allowedToAdd.allowed,
                                }
                              : null,
                      ]),
                  }
                : null,
            maximumCheck && maximumPerWebsiteCheck && !cannotCopy
                ? {
                      value: 'copy',
                      content: 'Копировать',
                      onAction: () => {
                          copySection({ sectionInstanceId: section.instance_id });
                      },
                  }
                : null,
            canBeParent
                ? {
                      value: 'makeParent',
                      content: section.section_type === 'usual' ? 'Сделать компонентом' : 'Сделать родителем',
                      onAction: () => {
                          makeConstructorSectionParent({ pageId, sectionInstanceId: section.instance_id });
                      },
                  }
                : null,
            canDetach
                ? {
                      value: 'detach',
                      content: 'Отвязать от компонента',
                      onAction: () => {
                          detachSection(pageId, section.instance_id);
                      },
                  }
                : null,
            allowedToDelete
                ? {
                      value: 'delete',
                      content: 'Удалить',
                      onAction: deleteSectionCallback,
                  }
                : null,
            canBeReplacedWithMystique
                ? {
                      key: 'replaceWithMystique',
                      options: [
                          {
                              value: 'replaceWithMystique',
                              content: 'Заменить секцию',
                              onAction: replaceWithMystique,
                          },
                      ],
                  }
                : null,
        ]);
    }, [
        section.section_type,
        section.widget,
        section.instance_id,
        section.display_sequence,
        allowedToDelete,
        copySection,
        copiedSection,
        sectionSettings,
        replaceSectionHandler,
        allowedToAdd,
        allowedToReplace,
        pageId,
        yandexKit,
        addModuleCallback,
        deleteSectionCallback,
        detachSection,
        denormalizeSection,
        areTemplatesEnabled,
        replaceWithMystique,
        checkForLimits,
        showLimit,
    ]);

    const moduleItems = useMemo(() => {
        if (!module || !moduleId) {
            return [];
        }

        const canDeleteCheck =
            get(sectionSettings, 'properties.modules.type') !== 'object' && section.section_type !== 'child';

        const canReplaceCheck =
            section.section_type !== 'child' &&
            copiedSection &&
            ((canDeleteCheck &&
                copiedSection?.widget === section.widget &&
                copiedSection?.moduleId &&
                copiedSection.moduleId !== moduleId) ||
                (!canDeleteCheck && copiedSection.moduleId === moduleId));

        const canAddNext = copiedSection && canDeleteCheck && copiedSection?.widget === section.widget;

        const isWrongWidget = copiedSection && copiedSection.widget !== section.widget && copiedSection.moduleId;
        const copyMessage = isWrongWidget ? (
            <>
                Элемент можно вставить только <LineBreak mode="compact" />в секцию{' '}
                {yandexKit.getSettings(
                    denormalizeSection({
                        ...copiedSection.sectionSettings,
                        instance_id: copiedSection.sectionInstanceId,
                    })
                )?.title || 'того же типа, из которого она была скопирована'}
            </>
        ) : null;

        const canCopyCheck =
            get(sectionSettings, 'properties.modules.type') !== 'object' ||
            (sectionSettings?.modulesCopyWhitelist && sectionSettings.modulesCopyWhitelist.includes(moduleId));

        return compact([
            canReplaceCheck || isWrongWidget
                ? {
                      key: 'copyOrReplace',
                      options: compact([
                          canReplaceCheck && {
                              value: 'replace',
                              content: 'Заменить',
                              onAction: () => {
                                  logEventString(
                                      constructorProductLoggingEvents.Section.ReplaceFromClipboard,
                                      'module',
                                      { widget: copiedSection.widget }
                                  );
                                  replaceModule(copiedSection.sectionSettings);
                              },
                          },
                          (canAddNext || isWrongWidget) && {
                              value: 'paste',
                              content: 'Вставить элемент ниже',
                              caption: copyMessage,
                              onAction: () => {
                                  logEventString(constructorProductLoggingEvents.Section.Paste, 'moduleBelow', {
                                      widget: copiedSection.widget,
                                  });
                                  addModuleCallback(copiedSection.sectionSettings, module.display_sequence + 0.01);
                              },
                              disabled: Boolean(isWrongWidget),
                          },
                      ]),
                  }
                : null,
            canCopyCheck && {
                value: 'copy',
                content: 'Копировать',
                onAction: () => {
                    copySection({ sectionInstanceId: section.instance_id, moduleId });
                },
            },
            canDeleteCheck && {
                value: 'delete',
                content: 'Удалить',
                onAction: () => {
                    deleteModuleCallback(moduleId);
                },
            },
        ]);
    }, [
        module,
        deleteModuleCallback,
        moduleId,
        copiedSection,
        sectionSettings,
        addModuleCallback,
        copySection,
        replaceModule,
        section.instance_id,
        section.widget,
        section.section_type,
        yandexKit,
        denormalizeSection,
    ]);

    const items = useMemo(() => {
        if (moduleId) {
            return moduleItems;
        }

        return sectionItems;
    }, [moduleId, moduleItems, sectionItems]);

    useEffect(() => {
        if (currentSection?.sectionInstanceId === sectionInstanceId) {
            checkCopiedSection();
        }
    }, [checkCopiedSection, currentSection?.sectionInstanceId, sectionInstanceId]);

    if (!items.length) {
        return null;
    }

    return (
        <VerticalMenu
            items={items}
            {...rest}
            triggerProps={{ ...rest?.triggerProps, onHoverStart: checkCopiedSection }}
            data-testid={moduleId ? constructorTestIds.moduleItemMenu : constructorTestIds.sectionItemMenu}
        />
    );
};
